import { useState } from "react";
import toast from "react-hot-toast";
import { CheckCircle2, Receipt, Wallet } from "lucide-react";
import { Button } from "@veorest/ui";
import { formatCurrency } from "@veorest/utils";
import { StatsStrip, type StatTileItem } from "../../data/StatsStrip";
import { formatShortDateTime } from "../../../lib/partnerReports";
import {
  usePartnerClientLedger,
  useRecordPartnerClientPayment,
} from "../../../hooks/usePartnerWallets";
import { mapWalletV2Error } from "../../../lib/partnerWallets";
import type { PartnerDashboardDateRange } from "../../../lib/partnerDashboardDate";
import { usePartnerSessionStore } from "../../../lib/partnerAuth";
import { useI18n } from "../../../i18n";

interface ClientStatementTabProps {
  tenantId: string;
  currency: string;
  range: PartnerDashboardDateRange;
}

export function ClientStatementTab({ tenantId, currency }: ClientStatementTabProps) {
  const { t, locale } = useI18n();
  const role = usePartnerSessionStore((s) => s.session?.role ?? "viewer");
  const canSettle = role === "owner" || role === "ops";

  const clientLedgerQuery = usePartnerClientLedger(tenantId);
  const ledgerTotals = clientLedgerQuery.data?.totals;
  const dueEntries = (clientLedgerQuery.data?.entries ?? []).slice(0, 20);
  const payments = clientLedgerQuery.data?.payments ?? [];
  const payMutation = useRecordPartnerClientPayment();
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");

  const money = (v: number) => formatCurrency(v, currency, locale);

  const payValue = Number(payAmount);
  const payValid = Number.isFinite(payValue) && payValue > 0;
  const payBalanceAfter =
    (ledgerTotals?.balance_to_pay ?? 0) - (payValid ? payValue : 0);

  const handleRecordPayment = async () => {
    if (!payValid) {
      toast.error(t("errors.amountInvalid"));
      return;
    }
    try {
      await payMutation.mutateAsync({ tenantId, amount: payValue, note: payNote });
      setPayAmount("");
      setPayNote("");
      toast.success(t("clientLedger.payToast", { amount: money(payValue) }));
    } catch (err) {
      toast.error(t(mapWalletV2Error(err)));
    }
  };

  const tiles: StatTileItem[] = [
    {
      key: "balanceToPay",
      label: t("clientLedger.balanceToPay"),
      value: ledgerTotals ? money(ledgerTotals.balance_to_pay) : "—",
      icon: Receipt,
      tone:
        ledgerTotals && ledgerTotals.balance_to_pay > 0 ? "accent" : "success",
    },
    {
      key: "feesTotal",
      label: t("clientLedger.feesTotal"),
      value: ledgerTotals ? money(ledgerTotals.fees_total) : "—",
      icon: Wallet,
      tone: "info",
    },
    {
      key: "paidTotal",
      label: t("clientLedger.paidTotal"),
      value: ledgerTotals ? money(ledgerTotals.paid_total) : "—",
      icon: CheckCircle2,
      tone: "ink",
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <StatsStrip
        tiles={tiles}
        loading={clientLedgerQuery.isLoading}
        columns={3}
      />

      {canSettle ? (
        <section className="menu-panel">
          <div className="menu-panel__body space-y-4">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
              {t("clientLedger.payTitle")}
            </h3>
            <span className="block text-[11px] font-semibold text-[var(--text-muted)]">
              {t("clientLedger.payHint")}
            </span>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  {t("clientLedger.payAmount")}
                </span>
                <input
                  className="input-base w-full"
                  type="number"
                  step="any"
                  min="0"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                />
              </div>
              <label className="block space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  {t("clientLedger.payNote")}
                </span>
                <input
                  className="input-base w-full"
                  value={payNote}
                  onChange={(e) => setPayNote(e.target.value)}
                />
              </label>
            </div>
            <div
              className="rounded-xl border px-3.5 py-3"
              style={{
                borderColor: "color-mix(in srgb, var(--accent) 35%, transparent)",
                background: "var(--accent-light)",
              }}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-secondary)]">
                  <Receipt className="h-3.5 w-3.5" aria-hidden />
                  {t("clientLedger.payTitle")}
                </span>
                <span className="tabular text-xl font-black text-[var(--text-primary)]">
                  {payValid ? money(payValue) : "—"}
                </span>
              </div>
              <p className="mt-1 text-[11px] font-semibold text-[var(--text-secondary)]">
                {payValid
                  ? `${t("clientLedger.payCurrent", {
                      amount: money(ledgerTotals?.balance_to_pay ?? 0),
                    })} · ${t("clientLedger.payAfter", {
                      amount: money(payBalanceAfter),
                    })}`
                  : t("walletV2.movePreviewHint")}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="primary"
                className="min-h-11 px-5"
                disabled={payMutation.isPending}
                onClick={() => void handleRecordPayment()}
              >
                {payMutation.isPending
                  ? t("common.loading")
                  : t("clientLedger.paySubmit")}
              </Button>
            </div>
            {payments.length > 0 ? (
              <div>
                <h4 className="mb-2 text-xs font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
                  {t("clientLedger.paymentsTitle")}
                </h4>
                <ul className="divide-y divide-[var(--border-subtle)]">
                  {payments.slice(0, 6).map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between gap-3 py-1.5"
                    >
                      <p className="text-[11px] text-[var(--text-muted)]">
                        {formatShortDateTime(p.created_at)}
                        {p.note ? ` · ${p.note}` : ""}
                      </p>
                      <span className="tabular text-xs font-bold text-[var(--text-primary)]">
                        {money(p.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="menu-panel">
        <div className="menu-panel__body space-y-3">
          <h3 className="text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
            {t("clientLedger.title")}
          </h3>
          {clientLedgerQuery.isLoading ? (
            <div className="h-16 rounded-xl skeleton" />
          ) : dueEntries.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">{t("clientLedger.empty")}</p>
          ) : (
            <ul className="divide-y divide-[var(--border-subtle)]">
              {dueEntries.map((entry) => (
                <li
                  key={entry.order_id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold text-[var(--text-primary)]">
                      #{entry.display_number || entry.order_id.slice(0, 8)}
                      {entry.return_to_store ? (
                        <span className="ms-2 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                          {t("clientLedger.returnToStore")}
                        </span>
                      ) : null}
                    </p>
                    <p className="text-[11px] text-[var(--text-muted)]">
                      {formatShortDateTime(
                        entry.rejected_at || entry.completed_at || entry.created_at
                      )}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-lg bg-[var(--accent-light)] px-2 py-1 text-xs font-bold tabular text-[var(--accent)]">
                    {money(entry.due_amount)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-[var(--text-muted)]">{t("clientLedger.note")}</p>
        </div>
      </section>

    </div>
  );
}
